// Insights/Blog Data
// Categories and articles for the Mundalo Insights section

export const insightCategories = [
  { id: 'nearshoring', name: 'Nearshoring', slug: 'nearshoring' },
  { id: 'cross-border', name: 'Cross-Border Strategy', slug: 'cross-border-strategy' },
  { id: 'development', name: 'Development', slug: 'development' },
  { id: 'relocation', name: 'Relocation', slug: 'relocation' }
];

export const insights = [
  {
    id: 'insight-1',
    slug: 'nearshoring-reshaping-mexican-industrial-real-estate',
    title: 'How Nearshoring is Reshaping Mexican Industrial Real Estate',
    excerpt: 'The shift from offshore to nearshore manufacturing is creating unprecedented demand for industrial space along the US-Mexico border and in key inland markets.',
    category: 'nearshoring',
    author: 'Mundalo Editorial',
    publishedAt: '2024-12-15',
    readTime: '6 min read',
    featured: true,
    seo: {
      title: 'Nearshoring & Mexican Industrial Real Estate | Mundalo',
      description: 'How the nearshoring boom is transforming industrial real estate demand in Mexico. Analysis of key markets, trends, and investment opportunities.'
    },
    heroImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1600&q=80',
    content: [
      {
        type: 'paragraph',
        text: 'The global supply chain realignment triggered by pandemic disruptions, geopolitical tensions, and rising Asian labor costs has accelerated a fundamental shift in manufacturing strategy. Companies that once viewed offshore production as the default are now actively pursuing nearshore alternatives-and Mexico sits at the center of this transformation.'
      },
      {
        type: 'heading',
        level: 2,
        text: 'The Numbers Behind the Shift'
      },
      {
        type: 'paragraph',
        text: 'Industrial vacancy rates in key Mexican markets have dropped to historic lows. Monterrey, the country\'s industrial heartland, saw vacancy fall below 2% in 2024. Tijuana, Juárez, and the Bajío region are experiencing similar compression. New construction can\'t keep pace with demand-a dynamic that\'s pushing rents higher and creating opportunities for developers who can deliver quality product.'
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80',
        alt: 'Modern industrial warehouse facility',
        caption: 'Class A industrial facilities are in high demand across Mexico\'s key manufacturing corridors.'
      },
      {
        type: 'heading',
        level: 2,
        text: 'Key Markets to Watch'
      },
      {
        type: 'paragraph',
        text: 'While border markets capture headlines, the real story is more nuanced. Interior markets like Guadalajara and Querétaro are emerging as tech manufacturing hubs. The Yucatán Peninsula is positioning itself for light manufacturing. And Mexico City\'s industrial submarkets are seeing renewed interest from logistics operators serving the domestic consumer market.'
      },
      {
        type: 'list',
        items: [
          'Monterrey: Automotive and heavy manufacturing, lowest vacancy nationally',
          'Tijuana: Electronics and medical devices, strong cross-border logistics',
          'Bajío (Querétaro, Guanajuato): Aerospace and automotive suppliers',
          'Guadalajara: Technology and electronics manufacturing'
        ]
      },
      {
        type: 'heading',
        level: 2,
        text: 'What This Means for Investors'
      },
      {
        type: 'paragraph',
        text: 'The nearshoring wave presents a multi-year tailwind for Mexican industrial real estate. However, execution matters. Success requires understanding local market dynamics, navigating regulatory complexity, and building relationships with the right operators. The window for opportunistic entry is narrowing as institutional capital flows into the sector.'
      },
      {
        type: 'cta',
        title: 'Exploring Industrial Investment in Mexico?',
        description: 'Our development advisory practice helps investors identify and execute industrial opportunities across key Mexican markets.',
        buttonText: 'View Our Services',
        buttonLink: '/services'
      }
    ]
  },
  {
    id: 'insight-2',
    slug: 'cross-border-real-estate-us-mexico-tax-considerations',
    title: 'Tax Considerations for US Citizens Buying Real Estate in Mexico',
    excerpt: 'Understanding the tax implications of Mexican property ownership is essential for US buyers. Here\'s what you need to know about FBAR, FATCA, and capital gains.',
    category: 'cross-border',
    author: 'Mundalo Editorial',
    publishedAt: '2024-11-28',
    readTime: '8 min read',
    featured: true,
    seo: {
      title: 'US Tax Guide for Mexican Real Estate | Mundalo',
      description: 'Essential tax considerations for US citizens buying property in Mexico. FBAR, FATCA, capital gains, and fideicomiso structures explained.'
    },
    heroImage: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=1600&q=80',
    content: [
      {
        type: 'paragraph',
        text: 'For US citizens and residents, purchasing real estate in Mexico triggers a complex web of reporting requirements and tax considerations on both sides of the border. Getting this wrong can result in significant penalties. Getting it right requires planning before you close.'
      },
      {
        type: 'heading',
        level: 2,
        text: 'The Fideicomiso Structure'
      },
      {
        type: 'paragraph',
        text: 'Mexican law restricts foreign ownership of property within 50 kilometers of the coast or 100 kilometers of the border-the "restricted zone." The workaround is a fideicomiso, a bank trust that holds title to the property on your behalf. You retain all beneficial rights: use, rental income, and the ability to sell. But the trust structure has US tax implications that many buyers overlook.'
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80',
        alt: 'Coastal property in Mexico',
        caption: 'Coastal properties in Mexico\'s restricted zone require a fideicomiso trust structure for foreign buyers.'
      },
      {
        type: 'heading',
        level: 2,
        text: 'FBAR and FATCA Reporting'
      },
      {
        type: 'paragraph',
        text: 'US persons with foreign financial accounts exceeding $10,000 at any point during the year must file an FBAR (FinCEN Form 114). While a fideicomiso isn\'t a bank account, the IRS has taken the position that it may trigger FBAR filing requirements. Similarly, FATCA (Form 8938) requires reporting of specified foreign financial assets above certain thresholds.'
      },
      {
        type: 'list',
        items: [
          'FBAR filing deadline: April 15 (automatic extension to October 15)',
          'FATCA thresholds: $50,000-$200,000 depending on filing status and residence',
          'Penalties for non-filing can exceed $10,000 per violation',
          'Willful violations carry criminal penalties'
        ]
      },
      {
        type: 'heading',
        level: 2,
        text: 'Capital Gains: Mexico vs. US'
      },
      {
        type: 'paragraph',
        text: 'When you sell Mexican property, you\'ll face capital gains tax in Mexico (typically 25-35% on the gain) and must report the sale on your US return. The foreign tax credit can offset double taxation, but the mechanics are complex. Mexican notarios often withhold taxes at closing-amounts that may or may not align with your actual US tax liability.'
      },
      {
        type: 'cta',
        title: 'Need Cross-Border Tax Guidance?',
        description: 'Our network includes US-Mexico tax specialists who help buyers structure acquisitions for optimal tax efficiency.',
        buttonText: 'View Our Partners',
        buttonLink: '/partners'
      }
    ]
  },
  {
    id: 'insight-3',
    slug: 'mexico-city-relocation-guide-2025',
    title: 'The Executive\'s Guide to Relocating to Mexico City in 2025',
    excerpt: 'From visa pathways to neighborhood selection, here\'s what professionals and families need to know about making the move to CDMX.',
    category: 'relocation',
    author: 'Mundalo Editorial',
    publishedAt: '2024-12-01',
    readTime: '10 min read',
    featured: false,
    seo: {
      title: 'Mexico City Relocation Guide 2025 | Mundalo',
      description: 'Complete guide to relocating to Mexico City. Visa options, neighborhoods, schools, healthcare, and cost of living for executives and families.'
    },
    heroImage: 'https://images.unsplash.com/photo-1614785432507-877be7f3c02e?auto=format&fit=crop&w=1600&q=80',
    content: [
      {
        type: 'paragraph',
        text: 'Mexico City has emerged as one of the world\'s most compelling destinations for professionals seeking a combination of career opportunity, cultural richness, and quality of life. Whether you\'re relocating for work, launching a business, or simply seeking a change, the city offers infrastructure and amenities that rival any global capital-often at a fraction of the cost.'
      },
      {
        type: 'heading',
        level: 2,
        text: 'Visa Pathways for Professionals'
      },
      {
        type: 'paragraph',
        text: 'Most professionals enter Mexico on a Temporary Resident visa, which allows stays of up to four years and can lead to permanent residency. The requirements are straightforward: proof of income or savings, a clean background check, and patience with bureaucracy. Remote workers can also use the Temporary Resident visa-Mexico doesn\'t have a specific "digital nomad" category, but the existing framework accommodates remote work well.'
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?auto=format&fit=crop&w=1200&q=80',
        alt: 'Mexico City skyline with Reforma avenue',
        caption: 'Paseo de la Reforma, the financial corridor connecting Polanco to the historic center.'
      },
      {
        type: 'heading',
        level: 2,
        text: 'Choosing Your Neighborhood'
      },
      {
        type: 'paragraph',
        text: 'Mexico City\'s neighborhoods vary dramatically in character. Polanco offers a polished, upscale environment with international schools and corporate offices within walking distance. Condesa and Roma attract a younger, creative crowd with their tree-lined streets and vibrant restaurant scene. Santa Fe provides a more suburban, American-style environment popular with families working in corporate settings.'
      },
      {
        type: 'list',
        items: [
          'Polanco: Executive housing, international schools, walkable luxury retail',
          'Condesa/Roma: Walkable urbanism, restaurants, younger professional crowd',
          'Santa Fe: Corporate campus environment, larger homes, American-style amenities',
          'San Ángel: Historic charm, established families, proximity to UNAM'
        ]
      },
      {
        type: 'heading',
        level: 2,
        text: 'Healthcare and Education'
      },
      {
        type: 'paragraph',
        text: 'Mexico City\'s private healthcare rivals US standards at significantly lower costs. Hospital ABC, Médica Sur, and Hospital Ángeles are equipped for complex procedures and staffed by specialists trained internationally. For families, international schools like the American School Foundation and Greengates provide US and UK curricula with strong university placement records.'
      },
      {
        type: 'cta',
        title: 'Planning Your Move to Mexico City?',
        description: 'Our relocation platform provides structured support for housing, visas, and daily life setup.',
        buttonText: 'Explore Relocation Services',
        buttonLink: '/relocation/cdmx'
      }
    ]
  }
];

// Helper functions
export const getInsightBySlug = (slug) => {
  return insights.find(i => i.slug === slug);
};

export const getInsightsByCategory = (categoryId) => {
  return insights.filter(i => i.category === categoryId);
};

export const getFeaturedInsights = () => {
  return insights.filter(i => i.featured);
};

export const getAllInsights = () => {
  return insights;
};

export const getCategoryBySlug = (slug) => {
  return insightCategories.find(c => c.slug === slug);
};

export const getCategoryById = (id) => {
  return insightCategories.find(c => c.id === id);
};
